import { ref, toValue, type MaybeRefOrGetter } from 'vue'
import { useI18n } from 'vue-i18n'
import type { GeoCoords } from '~/utils/buildFederatedSearchLabQueries'
import type { FederatedLabHit } from '~/components/FederatedSearch/helpers'
import type { CategoryNode } from '~/utils/pickSemanticCategories'
import { toSearchCity, type SearchCity } from '~/utils/searchCity'

export interface UseFederatedSearchTypesenseOptions {
  /**
   * Geo distance-sort seam (KFT-4027). Left plumbed but **not enabled** on the
   * homepage — pass a ref/getter of coords per surface to turn it on. When set,
   * events + locations are distance-sorted (see `buildFederatedSearchLabQueries`).
   */
  coords?: MaybeRefOrGetter<GeoCoords | undefined>
  /** Scopes events to this city (KFT-4205, the homepage hero's city line). */
  city?: MaybeRefOrGetter<SearchCity | null | undefined>
}

/** Category tree per categories collection (`categories_de`, …), shared across instances. */
const categoryTreeCache = new Map<string, Promise<CategoryNode[]>>()

/**
 * Name-matched categories first, then semantic ones (`pickSemanticCategories`)
 * fill the remaining slots — "kaffee rösten" matches no category by name but
 * gets Barista Kurs + Latte Art Kurs from the events it matched, and a kits-only
 * result gets its kits' categories (KFT-4145).
 */
const withSemanticCategories = (
  keywordHits: FederatedLabHit[],
  semantic: CategoryNode[],
  limit: number
): FederatedLabHit[] => {
  const seen = new Set(keywordHits.map((hit) => hit.slug))
  const extra = semantic
    .filter(({ slug }) => !seen.has(slug))
    .map(({ name, slug }) => ({
      objectID: slug,
      name,
      slug,
      _highlightResult: {},
    }))
  return [...keywordHits, ...extra].slice(0, limit)
}

/**
 * Production data layer for the Typesense federated search UI (KFT-4035).
 *
 * Runs the `multi_search` fan-out (`buildFederatedSearchLabQueries` +
 * `typesenseMultiSearch` + `mapFederatedSearchLabResponse`), then applies the
 * *production* caps the
 * Algolia `useFederatedSearchSources` used — events merged with kits into a
 * single column capped at `HITS_PER_PAGE.events`.
 *
 * Lazy by design: the heavy query/transport/mapper modules are **dynamically
 * imported inside `runSearch`**, so they stay out of the homepage's initial JS
 * and only load on the first search (i.e. first focus). Only the runtime config
 * is read eagerly (cheap).
 *
 * Events are searched exactly like /search?query= (KFT-4145): the shared
 * `buildEventsTextSearchParams` + `buildEventsRelevanceSort`, and the same
 * city-rule rerun. Surfaces that pass coords (the navbar does, once the
 * viewer's geo resolves) get nearby events first, each group still ordered by
 * relevance.
 * `eventsNbHits` mirrors Algolia's `nbHits` for the GA4 `search` event.
 */
export const useFederatedSearchTypesense = (
  options: UseFederatedSearchTypesenseOptions = {}
) => {
  const { locale } = useI18n()
  const {
    typesenseHost,
    typesensePort,
    typesenseProtocol,
    typesenseSearchApiKey,
  } = useRuntimeConfig().public

  const isConfigured = Boolean(typesenseHost)
  const host = `${typesenseProtocol}://${typesenseHost}:${typesensePort}`
  const apiKey = String(typesenseSearchApiKey ?? '')

  const locations = ref<FederatedLabHit[]>([])
  const categories = ref<FederatedLabHit[]>([])
  const events = ref<FederatedLabHit[]>([])
  const eventsNbHits = ref<number | null>(null)
  // The query the current results belong to — lets consumers tell a settled
  // query from one still debouncing/in flight (GA4 `view_item_list`).
  const resultsQuery = ref<string | null>(null)
  // The city the query itself names ("töpfern hamburg"), which wins over the
  // selected city for that search only (KFT-4205). Null when it names none.
  const queryCity = ref<SearchCity | null>(null)
  const isLoading = ref(false)
  // Only the latest search may write results, so a slow earlier response
  // can't overwrite (and be attributed to) the current query.
  let latestRequestId = 0

  // Resolves to whether this was still the latest search, i.e. whether its
  // outcome is what the panel now shows — callers must only act on true
  // (e.g. the GA4 `search` event), a superseded search was discarded.
  const runSearch = async (query: string): Promise<boolean> => {
    if (!isConfigured) return false
    const requestId = ++latestRequestId
    isLoading.value = true
    try {
      // Lazy-load the Typesense pipeline on first search so it stays out of the
      // homepage's initial bundle.
      const [
        { buildFederatedSearchLabQueries, SEMANTIC_CATEGORY_FACET },
        { typesenseMultiSearch, typesenseSearch },
        { mapFederatedSearchLabResponse },
        { mergeEventsWithKits, HITS_PER_PAGE },
        {
          pickSemanticCategories,
          toCategoryNodes,
          countDocumentCategories,
          mergeCategoryCounts,
        },
        { getCategoriesIndexName },
        { replaceGeoSortWithTextMatch },
      ] = await Promise.all([
        import('~/utils/buildFederatedSearchLabQueries'),
        import('~/utils/typesenseClient'),
        import('~/utils/mapFederatedSearchLabResponse'),
        import('~/components/FederatedSearch/helpers'),
        import('~/utils/pickSemanticCategories'),
        import('~/utils/searchEngineHelpers'),
        import('~/utils/typesenseGeo'),
      ])

      // The category tree (for parent/child pruning of semantic categories) is
      // fetched once per locale, alongside the first search. A failure only
      // costs the semantic suggestions, never the search itself.
      const categoriesCollection = getCategoriesIndexName(locale.value)
      let categoryTree = categoryTreeCache.get(categoriesCollection)
      if (!categoryTree) {
        categoryTree = typesenseSearch<any>(
          {
            collection: categoriesCollection,
            q: '*',
            query_by: 'name',
            per_page: 250,
            include_fields: 'name,slug,tree',
          },
          { host, apiKey }
        )
          .then((res) => {
            const nodes = toCategoryNodes(
              (res.hits ?? []).map((h) => h.document)
            )
            // Don't pin an empty tree for the session — retry on the next search.
            if (!nodes.length) categoryTreeCache.delete(categoriesCollection)
            return nodes
          })
          .catch((error) => {
            console.error('[federated-search-typesense] category tree', error)
            categoryTreeCache.delete(categoriesCollection)
            return []
          })
        categoryTreeCache.set(categoriesCollection, categoryTree)
      }

      const coords = toValue(options.coords)
      const city = toValue(options.city) ?? undefined
      const searches = buildFederatedSearchLabQueries({
        query,
        locale: locale.value,
        coords,
        city,
      })
      // Exclude admin areas (regions/states) from the locations source at query
      // level. Without this, the match-all default (`q:'*'`) returns 6 admin
      // areas that we then drop client-side, leaving the cities column empty on
      // first open. `searches[0]` is locations (FEDERATED_LAB_SOURCE_ORDER); the
      // client-side `admin_area` filter in the mapper stays as a safety net.
      // (Geoloc sort will layer on top of this later.) Empty query: cities
      // only — regions surface once the viewer types.
      if (searches[0]) {
        searches[0].filter_by = query.trim()
          ? 'type:!=admin_area'
          : 'type:=locality'
      }
      const results = await typesenseMultiSearch<any>(searches, {
        host,
        apiKey,
      })
      // The `auto-facet-cities` curation rule flags itself when the query
      // names a city. The viewer's distance sort would then rank events
      // nearest the viewer rather than in that city, so rerun events with the
      // nearby grouping swapped for text match — the same rerun /search does
      // (`useSearchIndex`), so both surfaces show the same events. Algolia's
      // city rule does the same via `aroundLatLngViaIP: false`. Index 2 is events.
      // The named city also replaces the selected one (KFT-4205): both
      // filters together would AND two cities into no results.
      const namesCity = Boolean(results[2]?.metadata?.skip_geo_sort)
      const sortWithoutGeo = namesCity
        ? replaceGeoSortWithTextMatch(searches[2]!.sort_by)
        : undefined
      if (sortWithoutGeo || (namesCity && city)) {
        const events = city
          ? buildFederatedSearchLabQueries({
              query,
              locale: locale.value,
              coords,
            })[2]!
          : searches[2]!
        ;[results[2]] = await typesenseMultiSearch<any>(
          [{ ...events, sort_by: sortWithoutGeo ?? events.sort_by }],
          { host, apiKey }
        )
      }
      // Awaited before the staleness check: nothing may await between that
      // check and the writes below, or a newer search could start in between.
      const categoryNodes = await categoryTree
      if (requestId !== latestRequestId) return false
      const mapped = mapFederatedSearchLabResponse(results, { query })

      locations.value = mapped.locations
      // The rule doesn't say which city it matched; the locations search on
      // the same text ranks it first.
      queryCity.value = namesCity
        ? toSearchCity(
            (results[0]?.hits ?? [])
              .map((hit) => hit.document)
              .find((doc) => doc?.type === 'locality')
          )
        : null
      // Semantic categories count the matched events (facet) and the
      // returned kits (their own `categories` — not facetable on
      // `products_*`), so a kits-only result still suggests categories.
      // Raw index 2 is events, 3 is products.
      const kitDocs = (results[3]?.hits ?? []).map((hit) => hit.document)
      categories.value = query.trim()
        ? withSemanticCategories(
            mapped.categories,
            pickSemanticCategories(
              mergeCategoryCounts(
                results[2]?.facet_counts?.find(
                  (f) => f.field_name === SEMANTIC_CATEGORY_FACET
                )?.counts ?? [],
                countDocumentCategories(kitDocs)
              ),
              categoryNodes,
              {
                found: (results[2]?.found ?? 0) + kitDocs.length,
                limit: HITS_PER_PAGE.categories,
              }
            ),
            HITS_PER_PAGE.categories
          )
        : mapped.categories
      // Events + kits share one column, exactly as `createEventsSource`'s
      // `transformResponse` merged them on the Algolia side.
      events.value = mergeEventsWithKits(
        mapped.events,
        mapped.kits,
        HITS_PER_PAGE.events
      )
      // Raw results follow FEDERATED_LAB_SOURCE_ORDER: [locations, categories,
      // events, products]; index 2 is the events collection.
      eventsNbHits.value = results?.[2]?.found ?? null
      resultsQuery.value = query
    } catch (error) {
      if (requestId !== latestRequestId) return false
      console.error('[federated-search-typesense] search failed', error)
      locations.value = []
      categories.value = []
      events.value = []
      eventsNbHits.value = null
      resultsQuery.value = null
      queryCity.value = null
    } finally {
      if (requestId === latestRequestId) isLoading.value = false
    }
    return requestId === latestRequestId
  }

  return {
    locations,
    categories,
    events,
    eventsNbHits,
    resultsQuery,
    queryCity,
    isLoading,
    runSearch,
    isConfigured,
  }
}
